import { Group, Stack, Text } from '@mantine/core';
import type { TFunction } from 'i18next';
import { DefaultValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Abstract } from '@/module/Author/Abstract';
import { AbstractCard } from '@/module/Author/Abstract/components/Card/AbstractCard';
import { Article } from '@/module/Author/Article';
import { ArticleCard } from '@/module/Author/Article/components/Card/ArticleCard';
import { Award } from '@/module/Author/Award';
import { AwardCard } from '@/module/Author/Award/components/Card/AwardCard';
import { Book } from '@/module/Author/Book';
import { BookCard } from '@/module/Author/Book/components/Card/BookCard';
import { Chapter } from '@/module/Author/Chapter';
import { ChapterCard } from '@/module/Author/Chapter/components/Card/ChapterCard';
import { CitationCard } from '@/module/Author/Citation/components/Card/CitationCard';
import { Citation } from '@/module/Author/Citation/model/CitationTypes';
import { Committee } from '@/module/Author/Committee';
import { CommitteeCard } from '@/module/Author/Committee/components/Card/CommitteeCard';
import { Course } from '@/module/Author/Course';
import { CourseCard } from '@/module/Author/Course/components/Card/CourseCard';
import { Patent } from '@/module/Author/Patent';
import { PatentCard } from '@/module/Author/Patent/components/Card/PatentCard';
import { Proceeding } from '@/module/Author/Proceeding';
import { ProceedingCard } from '@/module/Author/Proceeding/components/Card/ProceedingCard';
import { Project } from '@/module/Author/Project';
import { ProjectCard } from '@/module/Author/Project/components/Card/ProjectCard';
import { Translation } from '@/module/Author/Translation';
import { TranslationCard } from '@/module/Author/Translation/components/Card/TranslationCard';
import { BaseMinStdSelectControl } from '@/module/MinimalStandard/form/BaseMinStdSelectControl';
import {
  BaseIndicatorItemSchema,
  abstractIndicatorItemSchema,
  articleIndicatorItemSchema,
  awardIndicatorItemSchema,
  bookIndicatorItemSchema,
  booleanIndicatorItemSchema,
  chapterIndicatorItemSchema,
  citationIndicatorItemSchema,
  committeeIndicatorItemSchema,
  courseIndicatorItemSchema,
  freeIndicatorItemSchema,
  intervalIndicatorItemSchema,
  numberIndicatorItemSchema,
  patentIndicatorItemSchema,
  proceedingIndicatorItemSchema,
  projectIndicatorItemSchema,
  translationIndicatorItemSchema,
} from '@/module/MinimalStandard/model/MinStdSchema';
import { IndicatorItemRequest, MinStdIndicatorType } from '@/module/MinimalStandard/model/MinStdTypes';

import { NumberInputControl, TextInputControl } from '@/core/components';

import { Id, assertUnreachable } from '@feature/utils';

import { BaseMinStdIndicatorItemForm } from './BaseMinStdIndicatorItemForm';

interface BaseMinStdIndicatorItemFormProps<T extends BaseIndicatorItemSchema> {
  onSubmit: (request: IndicatorItemRequest) => void;
  onCancel: () => void;

  defaultValues: DefaultValues<T>;

  isSubmitting: boolean;

  indicatorType: MinStdIndicatorType;

  legend: string | null;

  authorId: Id;
  indicatorCode: string;
  year: number | null;

  alreadySelectedIds: number[];
  showPointsField: boolean;
}

function renderExtraFormFields(
  isSubmitting: boolean,
  indicatorType: MinStdIndicatorType,
  t: TFunction,
  legend: string | null,
  authorId: Id,
  indicatorCode: string,
  alreadySelectedIds: number[],
  year: number | null,
) {
  if (indicatorType === 'FREE_ANSWER') {
    return (
      <TextInputControl
        withAsterisk
        name="activityDescription"
        label={t('minStd.form.activityDescription')}
        isSubmitting={isSubmitting}
      />
    );
  }

  if (indicatorType === 'INTERVAL') {
    return (
      <Stack>
        <TextInputControl
          withAsterisk
          name="interval.activity"
          label={t('minStd.form.activityDescription')}
          isSubmitting={isSubmitting}
        />
        <Group>
          <NumberInputControl
            withAsterisk
            name="interval.startYear"
            label={t('minStd.form.startYear')}
            isSubmitting={isSubmitting}
          />
          <NumberInputControl
            name="interval.endYear"
            label={t('minStd.form.endYear')}
            isSubmitting={isSubmitting}
          />
        </Group>
      </Stack>
    );
  }

  if (indicatorType === 'BOOLEAN') {
    return (
      <Text
        fw="500"
        c="dimmed"
      >
        {legend}
      </Text>
    );
  }

  if (indicatorType === 'NUMBER') {
    return (
      <NumberInputControl
        withAsterisk
        name="number"
        label={legend ?? t('minStd.form.number')}
        isSubmitting={isSubmitting}
        allowDecimal={true}
        min={0}
      />
    );
  }

  if (indicatorType === 'SELECT_COURSE') {
    return (
      <BaseMinStdSelectControl<Course>
        label="Curs"
        renderItem={course => <CourseCard course={course} />}
        getTitle={course => course.title}
        getId={course => course.id}
        onView={course => {
          window.open(`/author/course/${course.id}`);
        }}
        authorId={authorId}
        indicatorCode={indicatorCode}
        name="course"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        year={year}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (indicatorType === 'SELECT_BOOK') {
    return (
      <BaseMinStdSelectControl<Book>
        label="Carte"
        renderItem={book => <BookCard book={book} />}
        getTitle={book => book.title}
        getId={book => book.id}
        onView={book => {
          window.open(`/author/book/${book.id}`);
        }}
        authorId={authorId}
        year={year}
        indicatorCode={indicatorCode}
        name="book"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (indicatorType === 'SELECT_ARTICLE') {
    return (
      <BaseMinStdSelectControl<Article>
        label="Articol"
        renderItem={article => <ArticleCard article={article} />}
        getTitle={article => article.title}
        getId={article => article.id}
        onView={article => {
          window.open(`/author/article/${article.id}`);
        }}
        authorId={authorId}
        year={year}
        indicatorCode={indicatorCode}
        name="article"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (indicatorType === 'SELECT_PROCEEDING') {
    return (
      <BaseMinStdSelectControl<Proceeding>
        label="Proceeding"
        renderItem={proceeding => <ProceedingCard proceeding={proceeding} />}
        getTitle={proceeding => proceeding.title}
        getId={proceeding => proceeding.id}
        onView={proceeding => {
          window.open(`/author/proceeding/${proceeding.id}`);
        }}
        authorId={authorId}
        year={year}
        indicatorCode={indicatorCode}
        name="proceeding"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (indicatorType === 'SELECT_TRANSLATION') {
    return (
      <BaseMinStdSelectControl<Translation>
        label="Traducere"
        renderItem={translation => <TranslationCard translation={translation} />}
        getTitle={translation => translation.title}
        getId={translation => translation.id}
        onView={translation => {
          window.open(`/author/translation/${translation.id}`);
        }}
        authorId={authorId}
        year={year}
        indicatorCode={indicatorCode}
        name="translation"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (indicatorType === 'SELECT_CHAPTER') {
    return (
      <BaseMinStdSelectControl<Chapter>
        label="Capitol"
        renderItem={chapter => <ChapterCard chapter={chapter} />}
        getTitle={chapter => chapter.title}
        getId={chapter => chapter.id}
        onView={chapter => {
          window.open(`/author/chapter/${chapter.id}`);
        }}
        authorId={authorId}
        year={year}
        indicatorCode={indicatorCode}
        name="chapter"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (indicatorType === 'SELECT_COMMITTEE') {
    return (
      <BaseMinStdSelectControl<Committee>
        label="Comisie"
        renderItem={committee => <CommitteeCard committee={committee} />}
        getTitle={committee => committee.title}
        getId={committee => committee.id}
        onView={committee => {
          window.open(`/author/committee/${committee.id}`);
        }}
        authorId={authorId}
        year={year}
        indicatorCode={indicatorCode}
        name="committee"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (indicatorType === 'SELECT_PROJECT') {
    return (
      <BaseMinStdSelectControl<Project>
        label="Proiect"
        renderItem={project => <ProjectCard project={project} />}
        getTitle={project => project.title}
        getId={project => project.id}
        onView={project => {
          window.open(`/author/project/${project.id}`);
        }}
        authorId={authorId}
        year={year}
        indicatorCode={indicatorCode}
        name="project"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (indicatorType === 'SELECT_CITATION') {
    return (
      <BaseMinStdSelectControl<Citation>
        label="Citare"
        renderItem={citation => <CitationCard citation={citation} />}
        getTitle={citation => citation.title}
        getId={citation => citation.id}
        authorId={authorId}
        year={year}
        indicatorCode={indicatorCode}
        name="citation"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (indicatorType === 'SELECT_AWARD') {
    return (
      <BaseMinStdSelectControl<Award>
        label="Premiu"
        renderItem={award => <AwardCard award={award} />}
        getTitle={award => award.title}
        getId={award => award.id}
        onView={award => {
          window.open(`/author/award/${award.id}`);
        }}
        authorId={authorId}
        year={year}
        indicatorCode={indicatorCode}
        name="award"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (indicatorType === 'SELECT_PATENT') {
    return (
      <BaseMinStdSelectControl<Patent>
        label="Brevet"
        renderItem={patent => <PatentCard patent={patent} />}
        getTitle={patent => patent.title}
        getId={patent => patent.id}
        onView={patent => {
          window.open(`/author/patent/${patent.id}`);
        }}
        authorId={authorId}
        year={year}
        indicatorCode={indicatorCode}
        name="patent"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (indicatorType === 'SELECT_ABSTRACT') {
    return (
      <BaseMinStdSelectControl<Abstract>
        label="Rezumat"
        renderItem={abstract => <AbstractCard abstract={abstract} />}
        getTitle={abstract => abstract.title}
        getId={abstract => abstract.id}
        onView={abstract => {
          window.open(`/author/abstract/${abstract.id}`);
        }}
        authorId={authorId}
        year={year}
        indicatorCode={indicatorCode}
        name="abstractItem"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  assertUnreachable(indicatorType);
}

function getSchema(indicatorType: MinStdIndicatorType) {
  if (indicatorType === 'FREE_ANSWER') {
    return freeIndicatorItemSchema;
  }

  if (indicatorType === 'BOOLEAN') {
    return booleanIndicatorItemSchema;
  }

  if (indicatorType === 'NUMBER') {
    return numberIndicatorItemSchema;
  }

  if (indicatorType === 'SELECT_COURSE') {
    return courseIndicatorItemSchema;
  }

  if (indicatorType === 'SELECT_ABSTRACT') {
    return abstractIndicatorItemSchema;
  }

  if (indicatorType === 'SELECT_ARTICLE') {
    return articleIndicatorItemSchema;
  }

  if (indicatorType === 'SELECT_COMMITTEE') {
    return committeeIndicatorItemSchema;
  }

  if (indicatorType === 'SELECT_PROJECT') {
    return projectIndicatorItemSchema;
  }

  if (indicatorType === 'SELECT_PATENT') {
    return patentIndicatorItemSchema;
  }

  if (indicatorType === 'SELECT_CHAPTER') {
    return chapterIndicatorItemSchema;
  }

  if (indicatorType === 'SELECT_BOOK') {
    return bookIndicatorItemSchema;
  }

  if (indicatorType === 'SELECT_TRANSLATION') {
    return translationIndicatorItemSchema;
  }

  if (indicatorType === 'SELECT_PROCEEDING') {
    return proceedingIndicatorItemSchema;
  }

  if (indicatorType === 'SELECT_AWARD') {
    return awardIndicatorItemSchema;
  }

  if (indicatorType === 'SELECT_CITATION') {
    return citationIndicatorItemSchema;
  }

  if (indicatorType === 'INTERVAL') {
    return intervalIndicatorItemSchema;
  }

  assertUnreachable(indicatorType);
}

export function MinStdIndicatorItemForm<T extends BaseIndicatorItemSchema>(props: BaseMinStdIndicatorItemFormProps<T>) {
  const {
    onSubmit,
    isSubmitting,
    onCancel,
    defaultValues,
    indicatorType,
    legend,
    authorId,
    indicatorCode,
    alreadySelectedIds,
    showPointsField,
    year,
  } = props;
  const { t } = useTranslation();
  return (
    <BaseMinStdIndicatorItemForm
      onCancel={onCancel}
      showPointsField={showPointsField}
      schema={getSchema(indicatorType)}
      onChange={onSubmit}
      isSubmitting={isSubmitting}
      defaultValues={defaultValues}
      renderExtraFields={() =>
        renderExtraFormFields(isSubmitting, indicatorType, t, legend, authorId, indicatorCode, alreadySelectedIds, year)
      }
    />
  );
}
